/**
 * F606DataPage — reusable table + pagination + search for any bridge endpoint.
 *
 * Pass in a `columns` array describing the fields to render for each row and
 * this component handles the heavy lifting (fetching, pagination, search).
 * The bridge always returns responses in the standard shape:
 *   { success, data: [], meta: { page, limit, total, pages } }
 *
 * Uses DashboardLayout so the page matches the admin panel theme exactly.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Search, Loader2, ChevronRight, ChevronLeft } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { getAuthHeaders } from '../../utils/authHeaders';
import { useLanguage } from '../../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function F606DataPage({
  entity,             // 'users' | 'orders' | 'products' ...
  titleAr,
  titleEn,
  icon: Icon,
  columns,            // [{ key, ar, en, render?, className? }]
  searchParamName = 'search', // some endpoints use `q` instead
  extraParams = {},   // e.g. { program_type: 'fitness' }
  emptyIcon: EmptyIcon,
}) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // debounce search
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(id);
  }, [search]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20', ...extraParams });
      if (debouncedSearch) params.set(searchParamName, debouncedSearch);
      const res = await axios.get(`${API}/api/f606/${entity}?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      const data = res.data?.data || [];
      setRows(Array.isArray(data) ? data : []);
      setMeta(res.data?.meta || { page, limit: 20, total: data.length, pages: 1 });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = (status === 404 || status === 502 || status === 503)
        ? (isAr ? 'تطبيق 606 فتنس غير متصل حالياً — تحقق من رابط الجسر' : '606 Fitness app is unreachable — check the bridge URL')
        : (typeof detail === 'string' ? detail : detail?.error?.message || detail?.message || err.message);
      toast.error(msg || (isAr ? 'فشل التحميل' : 'Failed to load'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [entity, page, debouncedSearch, JSON.stringify(extraParams), isAr, searchParamName]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <DashboardLayout
      title={titleAr}
      titleEn={titleEn}
      subtitle="بيانات حية من تطبيق 606 فتنس"
      subtitleEn="Live data from the 606 FITNESS app"
      screenCode={`F606-${entity.toUpperCase()}`}
      backPath="/control/f606"
      onBack={() => navigate('/control/f606')}
      breadcrumb={[
        { labelAr: 'التحكم والمعلومات', labelEn: 'Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: '606 فتنس', labelEn: '606 FITNESS', path: '/control/f606' },
        { labelAr: titleAr, labelEn: titleEn },
      ]}
    >
      <section dir={isAr ? 'rtl' : 'ltr'} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        {/* Header row: icon + count + refresh */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            {Icon && (
              <div className="w-10 h-10 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/30 flex items-center justify-center">
                <Icon className="h-5 w-5 text-[#d4af37]" />
              </div>
            )}
            <p className="text-xs text-[#71717A]">
              {loading ? (isAr ? 'جاري التحميل…' : 'Loading…') : `${meta.total ?? 0} ${isAr ? 'سجل' : 'records'}`}
            </p>
          </div>
          <button
            onClick={fetchRows}
            disabled={loading}
            className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-white/70 disabled:opacity-50 transition"
            data-testid="f606-page-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 ${isAr ? 'right-3' : 'left-3'}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={isAr ? 'ابحث…' : 'Search…'}
            className={`w-full bg-white/[0.03] border border-white/10 rounded-xl py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37]/40 ${isAr ? 'pr-9 pl-3' : 'pl-9 pr-3'}`}
            data-testid="f606-search"
          />
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl overflow-hidden">
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-[#d4af37]/60" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-white/50">
              {EmptyIcon && <EmptyIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />}
              <p className="text-sm">{isAr ? 'لا توجد سجلات' : 'No records'}</p>
              {debouncedSearch && (
                <p className="text-[11px] mt-1">
                  {isAr ? `لا نتائج لـ "${debouncedSearch}"` : `No matches for "${debouncedSearch}"`}
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] border-b border-white/10">
                  <tr>
                    {columns.map((c) => (
                      <th
                        key={c.key}
                        className={`px-3 py-2.5 text-[10px] font-black uppercase tracking-wider text-white/50 ${isAr ? 'text-right' : 'text-left'} ${c.className || ''}`}
                      >
                        {isAr ? c.ar : c.en}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr
                      key={row.id || i}
                      className="border-b border-white/[0.03] hover:bg-white/[0.02] transition"
                      data-testid={`f606-row-${row.id || i}`}
                    >
                      {columns.map((c) => (
                        <td key={c.key} className={`px-3 py-2 text-white/80 ${c.className || ''}`}>
                          {c.render ? c.render(row) : (row[c.key] ?? '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {meta.pages > 1 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/50">
              {isAr
                ? `الصفحة ${meta.page} من ${meta.pages}`
                : `Page ${meta.page} of ${meta.pages}`}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-30"
                data-testid="f606-prev"
              >
                {isAr ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </button>
              <button
                onClick={() => setPage((p) => Math.min(meta.pages, p + 1))}
                disabled={page >= meta.pages || loading}
                className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-30"
                data-testid="f606-next"
              >
                {isAr ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            </div>
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}
